import { useState, useEffect, useRef } from "react";
import { buildThumbUrl, buildSrcSet } from "@/utils/image";

/**
 * 双列瀑布流（对齐 BLW 项目 src/components/waterfalls/waterfalls.vue 的实现思路）
 *
 * 核心算法：
 *   ① 维护每列的累计高度 columnHeight[]
 *   ② 每条新数据放进「当前累计高度最小」的那一列 → 自然错落
 *   ③ 累加高度 = 图片等比缩放后的显示高度 + 文字区真实测量高度
 *
 * 与 BLW 的差异：
 *   - BLW 是 uni-app Vue3，用 boundingClientRect 逐条异步测量
 *   - 这里是 React + H5，改用 DOM 测量（getBoundingClientRect），
 *     并且一次性批量分配（避免逐条 await 造成的多次重排）
 */

export interface WaterfallItem {
  id: string | number;
  /** 封面图 */
  cover: string;
  /** 封面图原始宽高（用于等比预占位，防止图片加载后抖动） */
  coverWidth?: number;
  coverHeight?: number;
  /** 标题（最多 2 行） */
  title: string;
  /** 图片上的角标文字，如「专业导游」「名胜古迹」 */
  tag?: string;
  /** 角标样式变体 */
  tagVariant?: "dark" | "light";
  /**
   * 价格展示文案（优先级高于 price）
   * 真实接口的 price_text 形如「¥1999.00/人起」「面议」「查看房型」，
   * 非纯数字时用这个字段原样展示
   */
  priceText?: string;
  /** 价格（纯数字时展示，会拼成 ¥xxx 起） */
  price?: number;
  priceUnit?: string;
  /** 作者信息 */
  author?: {
    avatar?: string;
    nickname: string;
  };
  /** 点赞数 */
  likeCount?: number;
  /** 是否已点赞 */
  liked?: boolean;
  /** 原始业务数据 */
  raw?: any;
}

interface Props {
  items: WaterfallItem[];
  /** 列数，默认 2 */
  column?: number;
  /** 卡片点击 */
  onItemClick?: (item: WaterfallItem) => void;
}

/**
 * 无头像时的占位样式：按商品类型区分图标与配色
 *
 * 配色与首页「业务入口」四个图标保持一致（线路绿 / 导游紫 / 酒店蓝 / 租车橙），
 * 让用户在列表里也能一眼识别商品类型。
 */
const FALLBACK_AVATAR_STYLE: Record<string, { icon: string; color: string; bg: string }> = {
  tour: { icon: "ri-map-pin-2-fill", color: "#059669", bg: "#E7F6F0" },
  guide: { icon: "ri-user-star-fill", color: "#7C3AED", bg: "#F1EBFD" },
  hotel: { icon: "ri-hotel-fill", color: "#1E3A8A", bg: "#E8EEF9" },
  car: { icon: "ri-car-fill", color: "#D97706", bg: "#FDF2E3" },
};

/** 兜底样式（接口返回未知类型时使用） */
const FALLBACK_AVATAR_DEFAULT = {
  icon: "ri-map-pin-2-fill",
  color: "#F97316",
  bg: "#FFF1E6",
};

/** 从图片 URL 的 query 中解析 width / height（对齐 BLW 的 getUrlParams） */
function parseImageSize(url: string): { width: number; height: number } | null {
  if (!url) return null;
  const qIndex = url.indexOf("?");
  if (qIndex === -1) return null;
  const params = new URLSearchParams(url.slice(qIndex + 1));
  const w = Number(params.get("width"));
  const h = Number(params.get("height"));
  if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
    return { width: w, height: h };
  }
  return null;
}

export default function WaterfallList({
  items,
  column = 2,
  onItemClick,
}: Props) {
  // 每列的数据
  const [columns, setColumns] = useState<WaterfallItem[][]>([]);
  // 每列的累计高度
  const columnHeightRef = useRef<number[]>([]);
  // 已分配的 id，避免重复追加
  const assignedRef = useRef<Set<string | number>>(new Set());
  // 卡片文字区 DOM 引用（用于测量真实高度）
  const contentRefs = useRef<Map<string | number, HTMLDivElement>>(new Map());
  // 容器宽度（用于把图片原始宽高换算成显示高度）
  const containerRef = useRef<HTMLDivElement>(null);

  // 初始化列
  useEffect(() => {
    columnHeightRef.current = new Array(column).fill(0);
    setColumns(Array.from({ length: column }, () => []));
    assignedRef.current = new Set();
    contentRefs.current.clear();
  }, [column]);

  // 新增/变化的数据 → 分配到最短列
  //
  // ⚠️ 关键：分配逻辑必须写在 effect 体里（而非 setState 的 updater 内）。
  //    React 18 在 StrictMode 下会重复调用 updater 函数，
  //    若把「修改 ref / 累加列高」这类副作用放进 updater，会导致数据被重复追加。
  useEffect(() => {
    if (items.length === 0) return;
    if (columnHeightRef.current.length !== column) return;

    // 只处理尚未分配过的数据
    const pending = items.filter((it) => !assignedRef.current.has(it.id));
    if (pending.length === 0) return;

    // 容器内单列宽度（扣除 gap）：用于计算图片显示高度
    const containerW = containerRef.current?.clientWidth ?? 375;
    const gap = 12;
    const colWidth = (containerW - gap * (column - 1)) / column;

    // ① 先在纯计算中完成分配，产出「每条数据 → 目标列」的映射
    const targets: Array<{ item: WaterfallItem; minIdx: number }> = [];
    for (const item of pending) {
      const size =
        item.coverWidth && item.coverHeight
          ? { width: item.coverWidth, height: item.coverHeight }
          : parseImageSize(item.cover) || { width: 300, height: 300 };
      const imgDisplayHeight = (colWidth / size.width) * size.height;
      const totalHeight = imgDisplayHeight + estimateTextHeight(item);

      // 放进当前最短列
      let minIdx = 0;
      for (let i = 1; i < columnHeightRef.current.length; i++) {
        if (columnHeightRef.current[i] < columnHeightRef.current[minIdx]) {
          minIdx = i;
        }
      }

      // 预估高度立即累加，作为后续数据的分列依据
      columnHeightRef.current[minIdx] += totalHeight;
      targets.push({ item, minIdx });
    }

    // ② 统一标记已分配（放在 updater 之外，避免重复调用）
    pending.forEach((it) => assignedRef.current.add(it.id));

    // ③ 纯函数式更新列数据
    setColumns((prev) => {
      if (prev.length !== column) return prev;
      const next = prev.map((c) => [...c]);
      for (const { item, minIdx } of targets) {
        const size =
          item.coverWidth && item.coverHeight
            ? { width: item.coverWidth, height: item.coverHeight }
            : parseImageSize(item.cover) || { width: 300, height: 300 };
        next[minIdx].push({
          ...item,
          coverWidth: size.width,
          coverHeight: size.height,
        });
      }
      return next;
    });
  }, [items, column]);

  // 渲染后测量文字区真实高度，修正列高（供后续数据分配更准确）
  useEffect(() => {
    const containerW = containerRef.current?.clientWidth ?? 375;
    const gap = 12;
    const colWidth = (containerW - gap * (column - 1)) / column;

    columns.forEach((col, colIdx) => {
      // 重新累计该列高度（用真实 DOM 高度）
      let h = 0;
      for (const item of col) {
        const el = contentRefs.current.get(item.id);
        const textReal = el?.getBoundingClientRect().height ?? 0;
        const size =
          item.coverWidth && item.coverHeight
            ? { width: item.coverWidth, height: item.coverHeight }
            : parseImageSize(item.cover) || { width: 300, height: 300 };
        h += (colWidth / size.width) * size.height + textReal;
      }
      columnHeightRef.current[colIdx] = h;
    });
  }, [columns, column]);

  const gapPx = 12;

  return (
    <div
      ref={containerRef}
      className="px-5"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${column}, minmax(0, 1fr))`,
        gap: gapPx,
        alignItems: "start",
      }}
    >
      {columns.map((col, colIdx) => (
        <div
          key={colIdx}
          style={{ display: "flex", flexDirection: "column", gap: gapPx }}
        >
          {col.map((item, rowIdx) => (
            <WaterfallCard
              key={item.id}
              item={item}
              /* ★ 首屏前 4 张（两列各前 2 张）优先级最高，用 eager + high 抢加载 */
              priority={colIdx + rowIdx * column < 4}
              contentRef={(el) => {
                if (el) contentRefs.current.set(item.id, el);
                else contentRefs.current.delete(item.id);
              }}
              onClick={() => onItemClick?.(item)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** 文字区高度估算（首屏分配用，渲染后会被真实测量覆盖） */
function estimateTextHeight(item: WaterfallItem): number {
  let h = 8; // 上边距
  h += item.title ? (item.title.length > 16 ? 40 : 20) : 0;
  if (item.price != null) h += 26;
  if (item.author) h += 34; // 头像行
  h += 8;
  return h;
}

/** 单个瀑布流卡片 */
function WaterfallCard({
  item,
  priority = false,
  contentRef,
  onClick,
}: {
  item: WaterfallItem;
  /** 首屏高优先级：eager 加载 + fetchPriority=high（加速首屏可见图） */
  priority?: boolean;
  contentRef: (el: HTMLDivElement | null) => void;
  onClick: () => void;
}) {
  const [loaded, setLoaded] = useState(false);

  // 图片显示高度按原始宽高比预占位，避免加载完跳动
  const ratio =
    item.coverWidth && item.coverHeight
      ? item.coverHeight / item.coverWidth
      : 1;

  // 无头像时的占位样式（按商品类型取图标与配色）
  const fallback =
    FALLBACK_AVATAR_STYLE[item.raw?.type] || FALLBACK_AVATAR_DEFAULT;

  // ★ 图片压缩：默认「不生效」——实测本项目图片源均不支持参数化缩放。
  //   仅当 probeImageSupport() 探测确认某域名支持（或后端开通后手动标记）时，
  //   buildThumbUrl / buildSrcSet 才会真正改写 URL，否则原样返回原图。
  //   这样后端一旦开通能力，前端无需改代码即可自动受益。
  //   详见 src/utils/image.ts 顶部说明。
  const displayWidth = 170;
  const thumbSrc = buildThumbUrl(item.cover, displayWidth, ratio, 2);
  const srcSet = buildSrcSet(item.cover, displayWidth, ratio);

  return (
    <div
      onClick={onClick}
      className="bg-white overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
      style={{ borderRadius: 12, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}
    >
      {/* ── 封面图区 ── */}
      <div className="relative w-full" style={{ aspectRatio: `1 / ${ratio}` }}>
        {/* 未加载时的占位底色 */}
        {!loaded && (
          <div
            className="absolute inset-0"
            style={{ background: "#F3F4F6" }}
          />
        )}
        <img
          src={thumbSrc}
          srcSet={srcSet}
          alt={item.title}
          /* ★ 首屏 4 张 eager 抢占；其余 lazy 省流量 */
          loading={priority ? "eager" : "lazy"}
          /* ★ 注意：必须全小写 fetchpriority。
             React 18 不认识小驼峰 fetchPriority，会报警告且不生效 */
          {...(priority ? { fetchpriority: "high" as const } : {})}
          /* ★ 异步解码，避免图片解码阻塞主线程导致滚动卡顿 */
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            /* ★ 渐进显示：未加载完成时轻微模糊，加载完淡入，减少"突变"感 */
            filter: loaded ? "none" : "blur(6px)",
            transform: loaded ? "none" : "scale(1.04)",
            transition: "filter 300ms ease-out, transform 300ms ease-out",
          }}
        />

        {/* ── 角标 ── */}
        {item.tag && (
          <div
            className="absolute text-center whitespace-nowrap"
            style={
              item.tagVariant === "light"
                ? {
                    top: 8,
                    right: 8,
                    fontSize: 10,
                    color: "#333",
                    background: "#fff",
                    padding: "3px 8px",
                    borderRadius: 19,
                  }
                : {
                    bottom: 8,
                    left: 8,
                    fontSize: 10,
                    color: "#fff",
                    background: "rgba(0,0,0,0.5)",
                    padding: "3px 8px",
                    borderRadius: 19,
                  }
            }
          >
            {item.tag}
          </div>
        )}
      </div>

      {/* ── 文字区 ── */}
      <div ref={contentRef} className="px-2.5 pt-2 pb-2.5">
        {/* 标题：最多 2 行 */}
        <div
          className="text-[13px] leading-snug line-clamp-2"
          style={{ color: "#333", minHeight: 18 }}
        >
          {item.title}
        </div>

        {/* 价格：优先用接口给的 priceText（如「¥1999.00/人起」「面议」） */}
        {item.priceText ? (
          <div
            className="mt-1.5 truncate"
            style={{ fontSize: 13, fontWeight: 700, color: "#DB1818" }}
          >
            {item.priceText}
          </div>
        ) : item.price != null ? (
          <div className="flex items-baseline mt-1.5" style={{ color: "#DB1818" }}>
            <span style={{ fontSize: 10, fontWeight: 700 }}>￥</span>
            <span style={{ fontSize: 14, fontWeight: 700 }}>{item.price}</span>
            <span style={{ fontSize: 10, color: "#999", marginLeft: 2 }}>
              {item.priceUnit ? `元/${item.priceUnit}` : "起"}
            </span>
          </div>
        ) : null}

        {/* 作者行：头像 + 昵称 + 点赞 */}
        {item.author && (
          <div className="flex items-center mt-2">
            {item.author.avatar ? (
              <img
                src={item.author.avatar}
                alt=""
                className="rounded-full object-cover flex-shrink-0"
                style={{ width: 20, height: 20 }}
              />
            ) : (
              /* 接口未返回头像时的占位：类型图标 + 专属配色 */
              <div
                className="rounded-full flex-shrink-0 flex items-center justify-center"
                style={{ width: 20, height: 20, background: fallback.bg }}
              >
                <i
                  className={fallback.icon}
                  style={{ fontSize: 11, color: fallback.color }}
                />
              </div>
            )}
            <span
              className="ml-1.5 truncate"
              style={{ fontSize: 10, color: "#333", flex: 1, minWidth: 0 }}
            >
              {item.author.nickname}
            </span>
            <span className="flex items-center flex-shrink-0 ml-1">
              <i
                className={item.liked ? "ri-heart-3-fill" : "ri-heart-3-line"}
                style={{
                  fontSize: 12,
                  color: item.liked ? "#FE7A3A" : "#A6A8A7",
                }}
              />
              {!!item.likeCount && (
                <span style={{ fontSize: 10, color: "#A6A8A7", marginLeft: 2 }}>
                  {item.likeCount}
                </span>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
